import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { io as ioClient } from "socket.io-client";
import api, { API_ORIGIN } from "../api";
import {
  LayoutDashboard, FileText, FileEdit, MessageSquare, Users, Settings, LogOut, UserCog,
  Search, Bell, MessageCircle, Menu, X,
} from "lucide-react";
import { getUser, isAdmin, ROLE_LABEL, logoutUser } from "../auth";

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [user, setUser] = useState(getUser);
  const [globalSearch, setGlobalSearch] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => { setMobileNavOpen(false); }, [pathname]);
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (event) => { if (event.key === "Escape") setMobileNavOpen(false); };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = previous; };
  }, [mobileNavOpen]);
  const logout = useCallback(async () => { await logoutUser(); navigate("/", { replace: true }); }, [navigate]);

  const [unreadComments, setUnreadComments] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [attentionTotal, setAttentionTotal] = useState(0);
  const socketRef = useRef(null);

  // Keep the stored role/name in sync with the server (an admin may have
  // changed this person's role or disabled them since they logged in).
  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => {
        if (!data) return;
        if (data.isActive === false || !["admin", "worker"].includes(data.role)) return logout();
        const next = { ...getUser(), id: data._id, name: data.name, email: data.email, role: data.role };
        localStorage.setItem("user", JSON.stringify(next));
        setUser(next);
      })
      .catch((e) => { if (e.response?.status === 401) logout(); });
  }, [logout]);

  const loadCounts = useCallback(async () => {
    const [convos, notifs, stats] = await Promise.allSettled([
      api.get("/admin/conversations"),
      api.get("/notifications"),
      api.get("/requests/stats"),
    ]);
    if (convos.status === "fulfilled") setUnreadComments((convos.value.data || []).reduce((s, c) => s + (c.unreadCount || 0), 0));
    if (notifs.status === "fulfilled") setUnreadNotifications((notifs.value.data || []).filter((n) => !n.read).length);
    if (stats.status === "fulfilled") setAttentionTotal(stats.value.data?.attentionTotal || 0);
  }, []);

  useEffect(() => {
    loadCounts();
    const t = setInterval(loadCounts, 30000);
    return () => clearInterval(t);
  }, [loadCounts]);

  // live refresh of the badges
  useEffect(() => {
    const socket = ioClient(API_ORIGIN, { transports: ["websocket", "polling"], auth: { token: localStorage.getItem("token") } });
    socketRef.current = socket;
    socket.emit("join", "admin");
    let t;
    const soon = () => { clearTimeout(t); t = setTimeout(loadCounts, 400); };
    ["conversation:update", "request:new", "request:update", "quote:update"].forEach((ev) => socket.on(ev, soon));
    socket.on("notification:new", () => { setUnreadNotifications((n) => n + 1); soon(); });
    return () => { clearTimeout(t); socket.disconnect(); };
  }, [loadCounts]);

  const admin = isAdmin(user);
  const nav = [
    { icon: LayoutDashboard, label: "Dashboard", to: "/admin/dashboard" },
    { icon: FileText, label: "Requests", to: "/admin/requests", alert: attentionTotal },
    { icon: FileEdit, label: "Quotes", to: "/admin/quotes" },
    { icon: MessageSquare, label: "Comments", to: "/admin/comments", badge: unreadComments },
    ...(admin ? [
      { icon: Users, label: "Customers", to: "/admin/customers" },
      { icon: UserCog, label: "Team", to: "/admin/team" },
    ] : []),
  ];

  return (
    <div className="flex min-h-screen min-w-0 bg-slate-50">
      {mobileNavOpen && <button type="button" aria-label="Close navigation" className="fixed inset-0 z-[80] bg-black/60 lg:hidden" onClick={() => setMobileNavOpen(false)} />}
      {/* Sidebar */}
      <aside id="agp-admin-navigation" className={`fixed inset-y-0 left-0 z-[90] w-64 max-w-[85vw] bg-slate-900 text-white flex flex-col shrink-0 h-[100dvh] overflow-y-auto transition-transform duration-200 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="px-6 py-5 relative">
          <button type="button" aria-label="Close menu" className="absolute right-3 top-4 text-white lg:hidden" onClick={() => setMobileNavOpen(false)}><X size={22}/></button>
          <div className="font-extrabold text-lg">ALL G<span className="text-red-500">OO</span>D PARTS</div>
          <div className="text-[10px] tracking-widest text-slate-400 mt-1">SALES PANEL</div>
        </div>
        <nav className="px-3 mt-4 flex-1 space-y-1">
          {nav.map((n) => {
            const active = pathname === n.to || pathname.startsWith(`${n.to}/`);
            return (
              <button key={n.label} onClick={() => navigate(n.to)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm ${active ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/50"}`}>
                <n.icon size={18} />
                <span className="flex-1 text-left">{n.label}</span>
                {n.alert > 0 && (
                  <span className="relative flex items-center" title={`${n.alert} need attention`}>
                    <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60 motion-safe:animate-ping" />
                    <span className="relative text-[10px] font-bold bg-amber-400 text-slate-900 rounded-full px-1.5 py-0.5">{n.alert}</span>
                  </span>
                )}
                {n.badge > 0 && (
                  <span className="text-[10px] bg-red-500 text-white rounded-full px-1.5 py-0.5">{n.badge}</span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-slate-800 space-y-1">
          <button onClick={() => navigate("/admin/settings")}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-slate-300 hover:bg-slate-800/50">
            <Settings size={18} /> Settings
          </button>
          <button onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-slate-300 hover:bg-slate-800/50">
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-gray-100 px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-4 min-w-0">
          <button type="button" className="lg:hidden shrink-0 p-2 rounded-lg hover:bg-slate-100" aria-label="Open navigation" aria-expanded={mobileNavOpen} aria-controls="agp-admin-navigation" onClick={() => setMobileNavOpen(true)}><Menu size={22}/></button>
          <div className="flex-1 min-w-0 max-w-xl relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <form onSubmit={(e) => { e.preventDefault(); navigate(`/admin/requests?q=${encodeURIComponent(globalSearch.trim())}`); }}><input aria-label="Search requests by ID, customer, vehicle or part" value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none" placeholder="Search request ID, customer, part, VIN..." /></form>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-4 ml-auto">
            <button onClick={() => navigate("/admin/notifications")} className="relative" aria-label="Notifications">
              <Bell size={20} className="text-slate-500" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] rounded-full px-1">{unreadNotifications}</span>
              )}
            </button>
            <button onClick={() => navigate("/admin/comments")} className="relative" aria-label="Unread comments">
              <MessageCircle size={20} className="text-slate-500" />
              {unreadComments > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] rounded-full px-1">{unreadComments}</span>
              )}
            </button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-slate-800 text-white grid place-items-center text-xs font-bold">
                {(user?.name?.split(" ").map((w) => w[0]).join("").slice(0, 2) || "?").toUpperCase()}
              </div>
              <div className="hidden sm:block leading-tight">
                <div className="text-sm font-semibold text-slate-700">{user?.name || "Staff"}</div>
                <div className="text-[11px] text-slate-400">{ROLE_LABEL[user?.role] || ""}</div>
              </div>
            </div>
          </div>
        </header>
        <div className="flex-1 flex flex-col min-w-0 agp-admin-page">{children}</div>
        <footer className="border-t border-gray-100 px-3 sm:px-6 py-4 flex flex-wrap gap-2 justify-between text-xs text-slate-400">
          <span>All Rights Reserved. Powered by <b>AllGoodParts.</b></span>
          <span>Privacy Policy | Terms &amp; Conditions</span>
        </footer>
      </div>
    </div>
  );
}
